const stages = ['make', 'year', 'model', 'submodel', 'configuration'];
export const emptySelection = () => Object.fromEntries(stages.map(key => [key, '']));
export function changeSelection(state, key, value) {
  const index = stages.indexOf(key);
  return index < 0 ? { ...state } : Object.fromEntries(stages.map((name, position) => [name, position < index ? state[name] : position === index ? value : '']));
}
export function vehicleChoices(vehicles, state, key) {
  let choices = [];
  if (key === 'make') choices = vehicles.map(v => ({ value: v.make, label: v.make }));
  if (key === 'year' && state.make) choices = vehicles.filter(v => v.make === state.make).map(v => ({ value: String(v.year), label: String(v.year) }));
  if (key === 'model' && state.make && state.year) choices = vehicles.filter(v => v.make === state.make && String(v.year) === state.year).map(v => ({ value: v.model, label: v.model }));
  if (key === 'submodel' && state.make && state.year && state.model) choices = vehicles.filter(v => v.make === state.make && String(v.year) === state.year && v.model === state.model).map(v => ({ value: v.id, label: v.submodel }));
  return [...new Map(choices.map(c => [c.value, c])).values()].sort((a,b) => key === 'year' ? Number(b.value) - Number(a.value) : a.label.localeCompare(b.label, 'en', { numeric: true }));
}
export function selectedVehicle(vehicles, state) {
  return vehicles.find(v => v.id === state.submodel && v.make === state.make && String(v.year) === state.year && v.model === state.model) || null;
}
export function brakeChoices(records, vehicle) {
  if (!vehicle) return [];
  return records.filter(r => vehicle.oemIds.includes(r.id) && r.market === 'US' && r.modelYears?.includes(vehicle.year));
}
export function dimension(value, unknown, inches) {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value} mm`;
  return typeof inches === 'number' && Number.isFinite(inches) ? `${inches} in` : unknown;
}
export function contactValues(vehicle, record, source) {
  if (!vehicle) return null;
  const base = `${vehicle.year} ${vehicle.make} ${vehicle.submodel} | US vehicle catalogue: ${source?.url || ''}`;
  const brake = record ? ` | ${record.brakePackage} | Front: ${dimension(record.front?.diameterMm,'Not published',record.front?.diameterIn)} / ${dimension(record.front?.thicknessMm,'Not published')} | Rear: ${dimension(record.rear?.diameterMm,'Not published',record.rear?.diameterIn)} / ${dimension(record.rear?.thicknessMm,'Not published')} | OEM sources: ${(record.sources || []).map(s => s.url).join(' ; ')}` : ' | Brake package to be confirmed';
  return { make: vehicle.make, model: vehicle.submodel, year: String(vehicle.year), reference: base + brake + ' | CROSS-X compatibility requires confirmation' };
}

function initialize(panel) {
  if (panel.dataset.initialized) return;
  panel.dataset.initialized = 'true';
  const copy = JSON.parse(panel.querySelector('[data-vehicle-copy]').textContent);
  const selects = Object.fromEntries(stages.map(key => [key, panel.querySelector(`[data-vehicle-select="${key}"]`)]));
  const status = panel.querySelector('[data-vehicle-status]');
  const result = panel.querySelector('[data-vehicle-result]');
  const oemPanel = panel.querySelector('[data-vehicle-oem]');
  const packageField = panel.querySelector('[data-vehicle-package-field]');
  const useButton = panel.querySelector('[data-vehicle-use]');
  const form = document.getElementById(panel.dataset.formId);
  const manual = form?.querySelector('details[data-manual-vehicle]');
  const summary = form?.querySelector('[data-selected-summary]');
  const fields = { make: form?.querySelector('[name="contact[Make]"]'), model: form?.querySelector('[name="contact[Model and trim]"]'), year: form?.querySelector('[name="contact[Model year]"]') };
  let data = { vehicles: [], records: [], sources: [] }, state = emptySelection(), applied = null, loadError = false;
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  const referenceField = () => {
    if (!form) return null;
    let input = form.querySelector('[name="contact[OEM reference]"]');
    if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'contact[OEM reference]'; form.append(input); }
    return input;
  };
  const clearApplied = () => {
    if (applied) form?.dispatchEvent(new CustomEvent('crossx:vehicle-cleared'));
    if (applied) for (const key of ['make','model','year']) if (fields[key]?.value === applied[key]) fields[key].value = '';
    applied = null;
    const reference = referenceField(); if (reference) reference.value = '';
    if (summary) { summary.hidden = true; summary.textContent = ''; }
    if (manual) manual.open = false;
  };
  const fillOptions = (key, choices) => {
    const select = selects[key]; select.replaceChildren(new Option(copy[`choose_${key}`], ''));
    choices.forEach(({value,label}) => select.add(new Option(label,value)));
    select.disabled = !choices.length; select.value = state[key];
  };
  const put = (key,value) => { const node = result.querySelector(`[data-result="${key}"]`); if (node) node.textContent = value; };
  const sourceLink = (list, source) => {
    let url; try { url = new URL(source.url); } catch { return; }
    if (url.protocol !== 'https:') return;
    const li = document.createElement('li'), a = document.createElement('a'); a.href=url.href; a.target='_blank'; a.rel='noopener noreferrer'; a.textContent=source.title || url.hostname; li.append(a); list.append(li);
  };
  function render() {
    for (const key of stages.slice(0,4)) fillOptions(key,vehicleChoices(data.vehicles,state,key));
    const vehicle = selectedVehicle(data.vehicles,state);
    const packages = brakeChoices(data.records,vehicle);
    fillOptions('configuration',packages.map(r => ({value:r.id,label:r.brakePackage})));
    const record = packages.find(r => r.id === state.configuration);
    result.hidden = !vehicle; useButton.disabled = !vehicle;
    packageField.hidden = !packages.length;
    oemPanel.hidden = !record;
    if (!vehicle) { status.textContent = loadError ? copy.load_error : data.vehicles.length ? copy.prompt : copy.loading; return; }
    put('vehicle',`${vehicle.year} ${vehicle.make} ${vehicle.submodel}`);
    put('configuration',record ? record.brakePackage : packages.length ? copy.choose_brakes : copy.selected);
    const catalogLinks=result.querySelector('[data-vehicle-catalog-sources]'); catalogLinks.replaceChildren();
    const catalogSource=data.sources[vehicle.source]; if (catalogSource) sourceLink(catalogLinks,catalogSource);
    result.querySelectorAll('details').forEach(d => { d.open=false; });
    if (record) {
      put('front_diameter',dimension(record.front?.diameterMm,copy.not_published,record.front?.diameterIn));
      put('front_thickness',dimension(record.front?.thicknessMm,copy.not_published));
      put('rear_diameter',dimension(record.rear?.diameterMm,copy.not_published,record.rear?.diameterIn));
      put('rear_thickness',dimension(record.rear?.thicknessMm,copy.not_published));
      put('material',record.rotorMaterial || copy.not_published); put('market',record.market || copy.not_published);
      const notes = result.querySelector('[data-result-notes]'); notes.replaceChildren();
      (record.notes || []).forEach(note => { const li=document.createElement('li');li.textContent=note;notes.append(li); }); notes.hidden=!notes.childElementCount;
      const links = result.querySelector('[data-result-sources]'); links.replaceChildren(); (record.sources || []).forEach(source => sourceLink(links,source));
    }
    status.textContent = copy.ready;
  }
  Object.entries(selects).forEach(([key,select]) => select.addEventListener('change',() => { clearApplied();state=changeSelection(state,key,select.value);render(); }));
  panel.querySelector('[data-vehicle-reset]').addEventListener('click',() => { clearApplied();state=emptySelection();render();selects.make.focus(); });
  panel.querySelector('[data-vehicle-manual]').addEventListener('click',() => {
    clearApplied();state=emptySelection();render();status.textContent=copy.manual;
    if (manual) manual.open=true;
    if (form) { form.scrollIntoView({behavior:motion(),block:'start'});fields.year?.focus({preventScroll:true}); }
  });
  useButton.addEventListener('click',() => {
    const vehicle=selectedVehicle(data.vehicles,state);
    const record=brakeChoices(data.records,vehicle).find(r=>r.id===state.configuration);
    const values=contactValues(vehicle,record,data.sources[vehicle?.source]); if (!values || !form) return;
    for (const key of ['make','model','year']) if(fields[key]){fields[key].value=values[key];fields[key].dispatchEvent(new Event('change',{bubbles:true}));}
    referenceField().value=values.reference; applied=values;
    if(summary){summary.hidden=false;summary.textContent=`${values.year} ${values.make} ${values.model}${record ? ' · '+record.brakePackage : ''}`;}
    if(manual)manual.open=false; status.textContent=copy.added;
    panel.dispatchEvent(new CustomEvent('crossx:vehicle-selected',{bubbles:true,detail:{vehicle,record:record || null,modelYear:values.year}}));
    const nextField=form.querySelector('[data-build-vin]:not(:disabled)') || form.querySelector('[name="contact[name]"]');nextField?.scrollIntoView({behavior:motion(),block:'center'});nextField?.focus({preventScroll:true});
  });
  form?.addEventListener('crossx:vin-applied',()=>{applied=null;state=emptySelection();render();referenceField().value='';});
  Object.values(fields).forEach(field=>field?.addEventListener('input',()=>{if(!applied)return;referenceField().value='';if(summary){summary.hidden=true;summary.textContent='';}applied=null;}));
  const controller=new AbortController(),timeout=setTimeout(()=>controller.abort(),15000);
  fetch(panel.dataset.source,{signal:controller.signal,credentials:'omit'})
    .then(response=>{if(!response.ok)throw new Error('Unavailable');return response.json();})
    .then(next=>{if(next.version!==2 || !Array.isArray(next.vehicles) || !Array.isArray(next.records) || !Array.isArray(next.sources))throw new Error('Invalid catalogue');
      data={...next,vehicles:next.vehicles.filter(v=>v.id&&v.make&&v.model&&v.submodel&&Number.isInteger(v.year)&&v.year>=2010&&v.year<=2026&&Array.isArray(v.oemIds))};
      if(!data.vehicles.length)throw new Error('Empty catalogue');render();})
    .catch(()=>{loadError=true;render();if(manual)manual.open=true;})
    .finally(()=>clearTimeout(timeout));
}
if(typeof document!=='undefined'){
  const start=(scope=document)=>scope.querySelectorAll('[data-crossx-vehicle]').forEach(initialize);
  start();document.addEventListener('shopify:section:load',event=>start(event.target));
}
